/** Gemini REST call with the learner's own API key (stored only on this device). */
import {
  GEMINI_ENDPOINT,
  GEMINI_MODELS,
  GEMINI_TIMEOUT_MS,
  TutorError,
  buildRequest,
  errorFromStatus,
  parseResponse,
  shouldTryNextModel,
  withThinking,
  type ChatMessage,
  type TutorContext,
} from '../core/tutor.ts';
import { readString, writeString } from './storage.ts';

const KEY_STORAGE = 'soile.geminiKey';

export function getGeminiKey(): string | null {
  return readString(KEY_STORAGE);
}

export function setGeminiKey(key: string | null): void {
  writeString(KEY_STORAGE, key && key.trim() ? key.trim() : null);
}

/** Aborts when the caller aborts or after GEMINI_TIMEOUT_MS, whichever comes first. */
function timeoutSignal(outer?: AbortSignal): { signal: AbortSignal; timedOut: () => boolean; clear: () => void } {
  const controller = new AbortController();
  let expired = false;
  const timer = setTimeout(() => {
    expired = true;
    controller.abort();
  }, GEMINI_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  outer?.addEventListener('abort', onAbort, { once: true });
  if (outer?.aborted) controller.abort();
  return {
    signal: controller.signal,
    timedOut: () => expired,
    clear: () => {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onAbort);
    },
  };
}

/**
 * Tries GEMINI_MODELS in order with the learner's key: a model that is unavailable, out of
 * quota, too slow or returns an empty answer is skipped for the next one.
 */
async function generate(body: (model: string) => unknown, outer?: AbortSignal): Promise<string> {
  const key = getGeminiKey();
  if (!key) throw new TutorError('no_key');
  if (!navigator.onLine) throw new TutorError('offline');

  let lastError = new TutorError('unknown');
  for (const model of GEMINI_MODELS) {
    const timer = timeoutSignal(outer);
    try {
      const response = await fetch(`${GEMINI_ENDPOINT}/${model}:generateContent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-goog-api-key': key },
        body: JSON.stringify(body(model)),
        signal: timer.signal,
      });
      const text = await response.text();
      const code = errorFromStatus(response.status, text);
      if (code === null) return parseResponse(JSON.parse(text) as unknown);
      lastError = new TutorError(code);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (!timer.timedOut()) throw error;
        lastError = new TutorError('unavailable');
      } else if (error instanceof TutorError) {
        lastError = error;
      } else if (error instanceof SyntaxError) {
        lastError = new TutorError('unavailable');
      } else {
        throw new TutorError('offline');
      }
    } finally {
      timer.clear();
    }
    if (!shouldTryNextModel(lastError.code)) break;
  }
  throw lastError;
}

export function askTutor(
  ctx: TutorContext,
  history: readonly ChatMessage[],
  signal?: AbortSignal,
): Promise<string> {
  return generate((model) => buildRequest(model, ctx, history), signal);
}

/**
 * One-shot request expecting a JSON reply (lesson questions, answer grading). Same key,
 * models and error handling as the tutor chat; returns the raw text for core parsers.
 */
export function askJson(prompt: string, signal?: AbortSignal): Promise<string> {
  return generate(
    (model) => ({
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.4,
        maxOutputTokens: 2048,
        responseMimeType: 'application/json',
        ...withThinking(model),
      },
    }),
    signal,
  );
}
